/**
 * switchOrg — clears all scope state, re-authenticates to the new org,
 * re-fetches org data, then delegates to switchWorkspace for the default workspace.
 */

/**
 * @param {object} state        - DOMQL root state
 * @param {object} sdk          - SDK instance (must have switchOrg + organization + workspace)
 * @param {object} queryClient  - @symbo.ls/fetch queryClient
 * @param {string} newOrgId     - target organisation ID
 */
export const switchOrg = async (state, sdk, queryClient, newOrgId) => {
  if (!sdk) {
    // Without an SDK ref the rest is a fake switch — clearing state would
    // race with the auth subscriber and look like a partial success. Bail
    // loudly so the caller's wiring bug surfaces in the console.

    console.warn('[switchOrg] aborted — SDK reference missing on element context')
    return
  }

  // Abort-before-mutate: `sdk.switchOrg` persists to Mongo FIRST (it awaits
  // its own internal `setActiveOrganization`) and only mutates its local
  // context after that succeeds — see sdk/src/index.js `switchOrg`. Mirror
  // that contract here by calling it BEFORE wiping any local DOMQL state.
  // A transient failure (network blip, 403, …) now throws out of this
  // function with ZERO state mutated, so the caller's try/catch can surface
  // an error while the UI stays pointed at the previous org. Previously the
  // wipe ran first, so a failed `sdk.switchOrg` left the app with every
  // org/workspace/project key nulled out and no way back.
  await sdk.switchOrg(newOrgId)

  // Self-contained locals — frank.toJSON serializes each functions-file
  // export independently, so a bare reference to a module-scope binding
  // reads as undefined once this file is served to a consuming app
  // (project_frank_drops_module_local_helpers). Inlining also sidesteps the
  // cross-file WORKSPACE_KEYS/WORKSPACE_PROJECT_KEYS/PROJECT_KEYS name
  // collision with switchWorkspace.js/switchWorkspaceProject.js's own
  // module-scope copies — frank's esbuild dedup would otherwise rename one
  // to a suffixed twin and silently pick a winner across files.
  for (const k of ['workspace', 'workspaceMembers', 'workspaceIntegrations', 'workspaceBilling']) {
    state[k] = null
  }
  for (const k of ['workspaceProject']) state[k] = null
  for (const k of ['project', 'projectComponents', 'projectPages', 'projectState']) {
    state[k] = null
  }
  for (const k of ['org', 'permissions', 'orgMembers', 'orgRoles', 'orgTeams']) state[k] = null

  // queryClient is optional — shells that haven't wired @symbo.ls/fetch
  // yet (workspace shell on the `next` branch) won't have one.
  if (queryClient && typeof queryClient.clear === 'function') queryClient.clear()

  // SDK methods are flat — sdk.organization / sdk.auth / sdk.workspace
  // namespaces don't exist. Service routing is via the SERVICE_METHODS map
  // in sdk/src/utils/services.js, which exposes:
  //   sdk.getOrganization(id)         (organization service)
  //   sdk.getPermissions(...)         (auth service — only via dispatcher)
  //   sdk.listWorkspaces({ org })     (workspace service)
  // Permissions endpoint isn't shipped on every backend yet — failure
  // there shouldn't abort the switch (we only need org + workspaces to
  // bring the UI up). Fall back to null and let the auth subscriber's
  // jwt-claims-derived role gates run instead.
  const [org, permissions] = await Promise.all([
    sdk.getOrganization(newOrgId),
    Promise.resolve(
      typeof sdk.getPermissions === 'function'
        ? sdk.getPermissions(newOrgId)
        : sdk.execute?.('auth.permissions', 'list', [newOrgId])
    ).catch((err) => {
      console.warn('[switchOrg] permissions fetch failed (continuing):', err?.message || err)
      return null
    })
  ])
  state.org = org
  state.permissions = permissions

  const workspaces = await sdk.listWorkspaces({ organization: newOrgId }).catch((err) => {
    console.warn('[switchOrg] listWorkspaces failed:', err?.message || err)
    return []
  })
  state.workspaces = Array.isArray(workspaces) ? workspaces : workspaces?.data || []

  // Prefer the workspace the user last chose, when it belongs to the org being
  // entered. `workspaces[0]` unconditionally was wrong: entering an org via
  // `/w/<org-slug>` (a reload, or any cross-org reconcile) discarded the
  // persisted `localStorage.activeWorkspace` pointer and dropped the user back
  // onto the org's first workspace. auth.js already treats that pointer as "the
  // user's explicit choice + the canonical pointer" and lets it override even a
  // stale JWT claim — this path was the one place that ignored it, so a refresh
  // silently reverted the selection.
  //
  // Only honoured when the pointer resolves INSIDE this org's list; switching
  // orgs must never leave the user pointed at a foreign workspace.
  const wsId = (w) => String((w && (w.id || w._id)) || '')
  let target = state.workspaces[0] || null
  try {
    const { getActiveWorkspacePointer } = await import('../activeWorkspacePointer.js')
    const pinned = getActiveWorkspacePointer()
    if (pinned) {
      const match = state.workspaces.find((w) => wsId(w) === String(pinned))
      if (match) target = match
    }
  } catch (err) {
    console.warn('[switchOrg] active-workspace pointer unavailable:', err?.message || err)
  }

  if (target) {
    const { switchWorkspace } = await import('./switchWorkspace.js')
    await switchWorkspace(state, sdk, queryClient, wsId(target))
  }

  // No separate persist step here — `sdk.switchOrg` above already awaited
  // its own `setActiveOrganization` PATCH (Mongo + socket.io fan-out to
  // other sessions) BEFORE this function touched any local state. Calling
  // `sdk.setActiveOrganization` again here would be a redundant duplicate
  // write on every single org switch.
}
